import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { 
  Coins, 
  Shield, 
  Zap, 
  Crown, 
  Palette, 
  PackageCheck, 
  Flame, 
  Sparkles, 
  ShoppingBag, 
  Check, 
  ArrowUpRight, 
  Radiation, 
  Clock, 
  Lock,
  Boxes
} from 'lucide-react';
import { 
  GamificationProfile, 
  VaultShopItem, 
  UserConsumableItem, 
  Language, 
  Vibe 
} from '../types';
import { 
  VAULT_SHOP_CATALOGUE, 
  RARITY_COLORS 
} from '../data/gamificationEconomyConfig';
import { 
  purchaseVaultShopItem, 
  activateConsumableItem, 
  getUserConsumables, 
  getGamificationProfile,
  equipTheme
} from '../services/bioBoxService';
import { sfx } from '../services/audioSynthesis';
import { cn } from '../lib/utils';

interface RedemptionVaultProps {
  userId?: string;
  lang: Language;
  vibe: Vibe | string;
  tone: 'bro' | 'system';
  theme: 'light' | 'dark';
  skin: string;
  toast: {
    success: (msg: string, opt?: any) => void;
    error: (msg: string, opt?: any) => void;
    info: (msg: string, opt?: any) => void;
  };
  onBalanceUpdated?: (newBalance: number) => void;
  onThemeChanged?: (themeId: string) => void;
}

const THEME_METADATA: Record<string, { name: { ua: string; en: string }; desc: { ua: string; en: string }; icon: any; rarity: 'common' | 'rare' | 'epic' | 'legendary' | 'mythic' }> = {
  cyberpunk: {
    name: { ua: 'Тема: Кіберпанк Матриця', en: 'Theme: Cyberpunk Matrix' },
    desc: { ua: 'Неонові смарагдові/жовті HUD сітки та кібер-інтерфейс.', en: 'Neon matrix HUD, dark obsidian surfaces, and high-energy telemetry.' },
    icon: Palette,
    rarity: 'epic'
  },
  stalker: {
    name: { ua: 'Тема: S.T.A.L.K.E.R. Hazmat', en: 'Theme: S.T.A.L.K.E.R. Hazmat' },
    desc: { ua: 'Монохромний PDA інтерфейс та радіоактивний зелений лічильник.', en: 'Authentic radioactive monochrome PDA interface with Geiger HUD.' },
    icon: Radiation,
    rarity: 'epic'
  },
  anime: {
    name: { ua: 'Тема: Anime Edition', en: 'Theme: Anime Edition' },
    desc: { ua: 'Пелюстки сакури, яскраві квести та рожево-фіолетові неонові акценти.', en: 'Sakura blossoms, shonen quests & vibrant pastel HUD styling.' },
    icon: Sparkles,
    rarity: 'epic'
  },
  default: {
    name: { ua: 'Тема: Базова Смарагдова', en: 'Theme: Minimalist Emerald' },
    desc: { ua: 'Мінімалістичний чистий інтерфейс зі смарагдовими акцентами.', en: 'Clean responsive layout with signature emerald green accents.' },
    icon: Palette,
    rarity: 'common'
  }
};

export const RedemptionVault: React.FC<RedemptionVaultProps> = ({
  userId,
  lang,
  vibe,
  tone,
  theme,
  skin,
  toast,
  onBalanceUpdated,
  onThemeChanged
}) => {
  const [profile, setProfile] = useState<GamificationProfile | null>(null);
  const [consumables, setConsumables] = useState<UserConsumableItem[]>([]);
  const [activeCategory, setActiveCategory] = useState<'all' | 'passes' | 'boosters' | 'themes' | 'crates'>('all');
  const [isPurchasing, setIsPurchasing] = useState<string | null>(null);

  const isUA = lang === 'ua' || (lang as string).startsWith('uk');

  const refreshData = async () => {
    if (!userId) return;
    const p = await getGamificationProfile(userId);
    const c = await getUserConsumables(userId);
    setProfile(p);
    setConsumables(c);
  };

  useEffect(() => {
    refreshData();

    const handleProfileUpdate = () => {
      refreshData();
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('gamification-profile-updated', handleProfileUpdate);
      window.addEventListener('bio-boxes-updated', handleProfileUpdate);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('gamification-profile-updated', handleProfileUpdate);
        window.removeEventListener('bio-boxes-updated', handleProfileUpdate);
      }
    };
  }, [userId]);

  const handleEquipTheme = async (themeId: string) => {
    if (!userId) return;
    sfx.playUiClick();
    sfx.playRarityFanfare('rare');
    const res = await equipTheme(userId, themeId);
    if (res.success) {
      toast.success(isUA ? res.message.ua : res.message.en);
      if (onThemeChanged) {
        onThemeChanged(themeId);
      }
      await refreshData();
    } else {
      toast.error(isUA ? res.message.ua : res.message.en);
    }
  };

  const handlePurchase = async (item: VaultShopItem) => {
    if (!userId || !profile) return;

    if (profile.token_balance < item.tokenPrice) {
      toast.error(
        isUA ? 'Недостатньо Nutri-Токенів!' : 'Insufficient Nutri-Tokens!',
        { description: isUA ? `Потрібно ${item.tokenPrice} NTK, у вас ${profile.token_balance} NTK.` : `Requires ${item.tokenPrice} NTK, you have ${profile.token_balance} NTK.` }
      );
      return;
    }

    setIsPurchasing(item.id);
    sfx.playUiClick();

    try {
      const res = await purchaseVaultShopItem(userId, item);
      if (res.success) {
        sfx.playTokenClink(3);
        toast.success(
          isUA ? res.message.ua : res.message.en,
          { description: isUA ? `Списано ${item.tokenPrice} Nutri-Токенів.` : `Deducted ${item.tokenPrice} Nutri-Tokens.` }
        );
        await refreshData();
        if (onBalanceUpdated && profile) {
          onBalanceUpdated(profile.token_balance - item.tokenPrice);
        }
        if (item.actionType === 'unlock_theme' && item.payload.themeId && onThemeChanged) {
          onThemeChanged(item.payload.themeId);
        }
      } else {
        toast.error(isUA ? res.message.ua : res.message.en);
      }
    } catch (e) {
      console.error('[Vault] Purchase error:', e);
      toast.error(isUA ? 'Помилка транзакції' : 'Transaction error');
    } finally {
      setIsPurchasing(null);
    }
  };

  const handleActivateConsumable = async (item: UserConsumableItem) => {
    if (!userId) return;

    sfx.playUiClick();
    const res = await activateConsumableItem(userId, item);
    if (res.success) {
      sfx.playRarityFanfare('epic');
      toast.success(isUA ? res.message.ua : res.message.en);
      await refreshData();
    } else {
      toast.error(isUA ? res.message.ua : res.message.en);
    }
  };

  const isStalker = skin === 'stalker';
  const isLight = theme === 'light' && !isStalker;

  const filteredItems = VAULT_SHOP_CATALOGUE.filter(item => {
    if (activeCategory === 'all') return true;
    return item.category === activeCategory;
  });

  const tokenBalance = profile?.token_balance || 0;

  return (
    <div className="space-y-6 animate-fadeIn">
      
      {/* TOKEN BALANCE & HERO VAULT BANNER */}
      <div 
        className={cn(
          "p-5 sm:p-6 rounded-3xl border relative overflow-hidden flex flex-col sm:flex-row items-center justify-between gap-4 transition-all",
          isStalker ? "border-[#4ade80]/40 bg-black text-[#4ade80] font-mono shadow-[0_0_20px_rgba(74,222,128,0.15)]" :
          "bg-gradient-to-br from-emerald-50/80 via-white to-teal-50/40 dark:from-zinc-900/90 dark:via-zinc-900/90 dark:to-zinc-950 border-zinc-200 dark:border-zinc-800 text-zinc-900 dark:text-zinc-100 shadow-xs font-sans"
        )}
      >
        {/* Ambient Tech Grid & Floating Particles */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden z-0" aria-hidden="true">
          <div className={cn(
            "absolute inset-0 opacity-40 dark:opacity-30",
            isStalker ? "bg-tech-grid" : "bg-tech-grid"
          )} style={{ backgroundSize: '24px 24px' }} />
          <motion.div
            animate={{
              x: [0, 30, -20, 0],
              y: [0, -15, 10, 0],
              scale: [1, 1.25, 0.9, 1],
              opacity: [0.35, 0.6, 0.4, 0.35]
            }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
            className={cn(
              "absolute -top-12 -left-12 w-48 h-48 rounded-full blur-3xl",
              isStalker ? "bg-[#4ade80]/20" : "bg-emerald-500/20 dark:bg-emerald-400/25"
            )}
          />
          <motion.div
            animate={{
              x: [0, -25, 15, 0],
              y: [0, 20, -15, 0],
              scale: [1, 1.15, 0.95, 1],
              opacity: [0.3, 0.55, 0.3, 0.3]
            }}
            transition={{ duration: 12, repeat: Infinity, ease: "easeInOut", delay: 1 }}
            className={cn(
              "absolute -bottom-12 -right-12 w-52 h-52 rounded-full blur-3xl",
              isStalker ? "bg-amber-400/15" : "bg-teal-400/20 dark:bg-teal-500/20"
            )}
          />
        </div>

        <div className="space-y-1.5 text-center sm:text-left z-10">
          <div className="flex items-center justify-center sm:justify-start gap-2">
            <span className={cn(
              "w-2.5 h-2.5 rounded-full animate-pulse",
              isStalker ? "bg-[#4ade80] shadow-[0_0_8px_#4ade80]" :
              "bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]"
            )} />
            <span className={cn(
              "text-[10px] uppercase tracking-widest font-bold",
              isStalker ? "text-[#4ade80] font-mono" :
              "text-emerald-600 dark:text-emerald-400 font-sans"
            )}>
              {isUA ? 'СХОВИЩЕ ВИНАГОРОД ТА ОБМІНУ' : 'REDEMPTION VAULT PROTOCOL'}
            </span>
          </div>
          <h3 className={cn(
            "text-xl sm:text-2xl font-black tracking-wider uppercase",
            isStalker ? "text-[#4ade80] font-mono" :
            "text-zinc-900 dark:text-white font-sans"
          )}>
            {isUA ? 'ТОКЕН-ЕКОНОМІКА & ПРОПУСКИ' : 'NUTRI-TOKEN VAULT & PASSES'}
          </h3>
          <p className={cn(
            "text-xs max-w-md leading-relaxed",
            isStalker ? "text-[#4ade80]/80 font-mono" :
            "text-zinc-600 dark:text-zinc-400 font-sans"
          )}>
            {tone === 'bro'
              ? (isUA ? 'Витрачай зароблені токени на заморозку стріку, PRO-пропуски та корисні бонуси! ⚡' : 'Spend hard-earned tokens on streak shields, PRO passes, and useful bonuses! ⚡')
              : (isUA ? 'Конвертуйте Nutri-Токени в цифрові активи та захисні бустери.' : 'Redeem validated Nutri-Tokens for system passes and telemetry shields.')}
          </p>
        </div>

        {/* Live Token Badge Widget */}
        <div className={cn(
          "flex flex-col items-center sm:items-end justify-center p-3.5 sm:p-4 rounded-2xl shrink-0 shadow-sm min-w-[170px] z-10 border transition-all",
          isStalker ? "bg-black border-[#4ade80]/40 font-mono" :
          (isLight ? "bg-white border-zinc-200/90" : "bg-zinc-900/90 border-zinc-800")
        )}>
          <span className={cn(
            "text-[9px] font-bold uppercase tracking-wider mb-0.5",
            isStalker ? "font-mono text-[#4ade80]/70" :
            "text-zinc-500 dark:text-zinc-400 font-sans"
          )}>
            {isUA ? 'БАЛАНС ТОКЕНІВ' : 'TOKEN BALANCE'}
          </span>
          <div className="flex items-center gap-2">
            <Coins className={cn(
              "w-6 h-6 animate-spin",
              isStalker ? "text-[#4ade80]" : "text-amber-500"
            )} style={{ animationDuration: '8s' }} />
            <span className={cn(
              "text-2xl sm:text-3xl font-black tracking-tight",
              isStalker ? "font-mono text-[#4ade80]" :
              "text-emerald-600 dark:text-emerald-400 font-sans"
            )}>
              {tokenBalance.toLocaleString()}
            </span>
            <span className={cn(
              "text-xs font-bold",
              isStalker ? "font-mono text-zinc-400" : "text-zinc-500 dark:text-zinc-400 font-sans"
            )}>NTK</span>
          </div>
        </div>
      </div>

      {/* ACTIVE INVENTORY & UNLOCKED REWARDS DRAWER */}
      {(() => {
        const unlockedThemeIds = (profile?.unlocked_themes || ['default']).filter(Boolean);
        const hasThemes = unlockedThemeIds.length > 0;
        const hasConsumables = consumables.length > 0;
        const hasAnyItems = hasThemes || hasConsumables;

        if (!hasAnyItems) return null;

        return (
          <div className={cn(
            "p-4 sm:p-5 rounded-2xl border space-y-4 transition-all",
            isStalker ? "border-[#4ade80]/30 bg-black" :
            (isLight ? "border-zinc-200 bg-zinc-50/70" : "border-zinc-800 bg-zinc-900/60")
          )}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Boxes className={cn(
                  "w-4 h-4",
                  isStalker ? "text-[#4ade80]" :
                  "text-emerald-600 dark:text-emerald-400"
                )} />
                <h4 className={cn(
                  "text-xs sm:text-sm font-bold uppercase tracking-wider",
                  isStalker ? "font-mono text-[#4ade80]" :
                  "text-zinc-900 dark:text-white font-sans"
                )}>
                  {isUA ? 'ВАШІ АКТИВНІ ПРЕДМЕТИ ТА РОЗБЛОКОВАНІ ТЕМИ' : 'YOUR ACTIVE ITEMS & UNLOCKED THEMES'}
                </h4>
              </div>
              <span className={cn(
                "text-[10px]",
                isStalker ? "font-mono text-zinc-400" : "text-zinc-500 dark:text-zinc-400 font-sans"
              )}>
                {unlockedThemeIds.length + consumables.length} {isUA ? 'в наявності' : 'in inventory'}
              </span>
            </div>

            {/* Unlocked Themes & Consumables Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2.5">
              {/* 1. Unlocked UI Themes */}
              {unlockedThemeIds.map(themeId => {
                const meta = THEME_METADATA[themeId] || {
                  name: { ua: `Тема: ${themeId}`, en: `Theme: ${themeId}` },
                  desc: { ua: 'Розблокований дизайн інтерфейсу.', en: 'Unlocked user interface theme.' },
                  icon: Palette,
                  rarity: 'epic' as const
                };
                const IconComponent = meta.icon;
                const isEquipped = skin === themeId;

                return (
                  <div
                    key={`theme_${themeId}`}
                    className={cn(
                      "flex items-center justify-between p-3 rounded-xl border transition-all",
                      isEquipped
                        ? (isStalker ? "bg-[#4ade80]/10 border-[#4ade80] shadow-[0_0_12px_rgba(74,222,128,0.2)]" :
                           (isLight ? "bg-emerald-50/80 border-emerald-400 shadow-xs" : "bg-emerald-950/30 border-emerald-500/50"))
                        : (isStalker ? "bg-black border-[#4ade80]/30 hover:border-[#4ade80]/60" :
                           (isLight ? "bg-white border-zinc-200 hover:border-emerald-500/40 shadow-xs" : "bg-zinc-900/80 border-zinc-800 hover:border-zinc-700"))
                    )}
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className={cn(
                        "w-9 h-9 rounded-lg border flex items-center justify-center shrink-0",
                        isEquipped
                          ? (isStalker ? "bg-[#4ade80]/20 border-[#4ade80] text-[#4ade80]" :
                             "bg-emerald-500/20 border-emerald-500 text-emerald-600 dark:text-emerald-400")
                          : (isStalker ? "bg-[#4ade80]/15 border-[#4ade80]/40 text-[#4ade80]" :
                             "bg-purple-500/10 border-purple-500/20 text-purple-600 dark:text-purple-400")
                      )}>
                        <IconComponent className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <span className={cn(
                          "text-xs font-bold block truncate",
                          isStalker ? "font-mono text-[#4ade80]" :
                          "text-zinc-900 dark:text-white font-sans"
                        )}>
                          {isUA ? meta.name.ua : meta.name.en}
                        </span>
                        <span className={cn(
                          "text-[10px] block font-semibold",
                          isEquipped 
                            ? (isStalker ? "font-mono text-[#4ade80]" : "text-emerald-600 dark:text-emerald-400 font-sans")
                            : (isStalker ? "font-mono text-zinc-400" : "text-zinc-500 dark:text-zinc-400 font-sans")
                        )}>
                          {isEquipped 
                            ? (isUA ? '● АКТИВОВАНО' : '● ACTIVE')
                            : (isUA ? 'Вільний доступ' : 'Unlocked')}
                        </span>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => handleEquipTheme(themeId)}
                      disabled={isEquipped}
                      className={cn(
                        "px-2.5 py-1 rounded-lg font-bold text-[10px] uppercase tracking-wider transition-all shrink-0 shadow-sm flex items-center gap-1",
                        isEquipped
                          ? (isStalker ? "bg-[#4ade80]/20 text-[#4ade80] border border-[#4ade80]/40 cursor-default font-mono" :
                             "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30 cursor-default font-sans")
                          : (isStalker ? "bg-[#4ade80] hover:bg-[#22c55e] text-black font-mono font-bold cursor-pointer active:scale-95" :
                             "bg-emerald-500 hover:bg-emerald-600 text-white font-sans cursor-pointer active:scale-95")
                      )}
                    >
                      {isEquipped ? (
                        <>
                          <Check className="w-3 h-3" />
                          <span>{isUA ? 'ОБРАНО' : 'ACTIVE'}</span>
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-3 h-3" />
                          <span>{isUA ? 'ОДЯГНУТИ' : 'EQUIP'}</span>
                        </>
                      )}
                    </button>
                  </div>
                );
              })}

              {/* 2. Active Consumables & Passes */}
              {consumables.map(item => (
                <div 
                  key={item.id}
                  className={cn(
                    "flex items-center justify-between p-3 rounded-xl border transition-all",
                    isStalker ? "bg-black border-[#4ade80]/30 hover:border-[#4ade80]/60" :
                    (isLight ? "bg-white border-zinc-200 hover:border-emerald-500/40 shadow-xs" : "bg-zinc-900/80 border-zinc-800 hover:border-zinc-700")
                  )}
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className={cn(
                      "w-9 h-9 rounded-lg border flex items-center justify-center shrink-0",
                      isStalker ? "bg-[#4ade80]/15 border-[#4ade80]/40 text-[#4ade80]" :
                      "bg-emerald-500/10 border-emerald-500/20 text-emerald-600 dark:text-emerald-400"
                    )}>
                      {item.type.startsWith('pro') ? <Crown className="w-4 h-4" /> : <Shield className="w-4 h-4" />}
                    </div>
                    <div className="min-w-0">
                      <span className={cn(
                        "text-xs font-bold block truncate",
                        isStalker ? "font-mono text-[#4ade80]" :
                        "text-zinc-900 dark:text-white font-sans"
                      )}>
                        {isUA ? item.name.ua : item.name.en}
                      </span>
                      <span className={cn(
                        "text-[10px] block",
                        isStalker ? "font-mono text-zinc-400" : "text-zinc-500 dark:text-zinc-400 font-sans"
                      )}>
                        {isUA ? 'Кількість:' : 'Qty:'} {item.count}x
                      </span>
                    </div>
                  </div>

                  {item.type.startsWith('pro') && (
                    <button
                      type="button"
                      onClick={() => handleActivateConsumable(item)}
                      className={cn(
                        "px-2.5 py-1 rounded-lg font-bold text-[10px] uppercase tracking-wider transition-all cursor-pointer shrink-0 shadow-sm",
                        isStalker ? "bg-[#4ade80] hover:bg-[#22c55e] text-black font-mono font-bold" :
                        "bg-emerald-500 hover:bg-emerald-600 text-white font-sans"
                      )}
                    >
                      {isUA ? 'АКТИВУВАТИ' : 'USE NOW'}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* SHOP CATEGORY FILTER TABS */}
      <div className={cn(
        "flex flex-wrap gap-1.5 border-b pb-3",
        isStalker ? "border-[#4ade80]/20 font-mono" :
        "border-zinc-200 dark:border-zinc-800 font-sans"
      )}>
        {[
          { id: 'all', label: isUA ? 'Усі товари' : 'All Vault Items' },
          { id: 'passes', label: isUA ? 'PRO Пропуски' : 'PRO Passes' },
          { id: 'boosters', label: isUA ? 'Захист & Бустери' : 'Shields & Boosters' },
          { id: 'themes', label: isUA ? 'Теми' : 'UI Themes' },
          { id: 'boxes', label: isUA ? 'Біо-Бокси' : 'Bio-Box Drops' }
        ].map(tab => (
          <button
            key={tab.id}
            type="button"
            onClick={() => {
              sfx.playUiClick();
              setActiveCategory(tab.id as any);
            }}
            className={cn(
              "px-3.5 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-all cursor-pointer border",
              activeCategory === tab.id
                ? (isStalker ? "bg-[#4ade80] text-black border-[#4ade80] font-mono font-bold" :
                   "bg-emerald-500 text-white border-emerald-500 shadow-sm font-sans font-semibold")
                : (isStalker ? "bg-black border-[#4ade80]/30 text-[#4ade80]/70 hover:text-[#4ade80] hover:bg-zinc-900/40 font-mono" :
                   "bg-zinc-100 dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white font-sans")
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* SHOP CATALOGUE CARDS GRID */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredItems.map(item => {
          const rarityCfg = RARITY_COLORS[item.rarity];
          const canAfford = tokenBalance >= item.tokenPrice;
          
          const isThemeItem = item.category === 'themes' || item.actionType === 'unlock_theme';
          const themeId = item.payload?.themeId;
          const isThemeOwned = Boolean(isThemeItem && themeId && (profile?.unlocked_themes?.includes(themeId) || themeId === 'default'));
          const isThemeActive = Boolean(isThemeOwned && skin === themeId);

          return (
            <div 
              key={item.id}
              className={cn(
                "p-5 rounded-2xl border transition-all flex flex-col justify-between relative group",
                isThemeActive
                  ? (isStalker ? "bg-black border-[#4ade80] shadow-[0_0_20px_rgba(74,222,128,0.25)] font-mono" :
                     (isLight ? "bg-white border-emerald-500 shadow-md ring-1 ring-emerald-500/20 font-sans" : "bg-zinc-900 border-emerald-500/70 shadow-md ring-1 ring-emerald-500/30 font-sans"))
                  : isThemeOwned
                  ? (isStalker ? "bg-black hover:border-[#4ade80]/80 border-[#4ade80]/40 font-mono" :
                     (isLight ? "bg-white border-zinc-200 hover:border-emerald-500/50 shadow-xs hover:shadow-md font-sans" : "bg-zinc-900/90 border-zinc-800 hover:border-zinc-700 shadow-xs font-sans"))
                  : isStalker ? "bg-black hover:border-[#4ade80]/70 border-[#4ade80]/30 font-mono" :
                  (isLight ? "bg-white border-zinc-200 hover:border-emerald-500/50 shadow-xs hover:shadow-md font-sans" : "bg-zinc-900/90 border-zinc-800 hover:border-zinc-700 shadow-xs font-sans")
              )}
            >
              {/* Card Top: Rarity, Ownership & Price */}
              <div className="flex items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className={cn(
                    "px-2.5 py-0.5 rounded-md border text-[9px] font-bold uppercase tracking-widest",
                    isStalker ? "font-mono bg-[#4ade80]/10 border-[#4ade80]/30 text-[#4ade80]" : cn("font-sans", rarityCfg.bg, rarityCfg.border, rarityCfg.text)
                  )}>
                    {item.rarity}
                  </span>

                  {isThemeOwned && (
                    <span className={cn(
                      "px-2 py-0.5 rounded-md border text-[9px] font-bold uppercase tracking-widest flex items-center gap-1",
                      isThemeActive
                        ? (isStalker ? "bg-[#4ade80]/20 border-[#4ade80] text-[#4ade80] font-mono" :
                           "bg-emerald-500/20 border-emerald-500 text-emerald-600 dark:text-emerald-400 font-sans")
                        : (isStalker ? "bg-[#4ade80]/10 border-[#4ade80]/30 text-[#4ade80]/80 font-mono" :
                           "bg-emerald-500/10 border-emerald-500/30 text-emerald-600 dark:text-emerald-400 font-sans")
                    )}>
                      <Check className="w-2.5 h-2.5" />
                      <span>{isThemeActive ? (isUA ? 'АКТИВНА' : 'ACTIVE') : (isUA ? 'ВІДКРИТО' : 'OWNED')}</span>
                    </span>
                  )}
                </div>

                {!isThemeOwned && (
                  <div className={cn(
                    "flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold border",
                    isStalker ? "bg-black border-[#4ade80]/40 text-[#4ade80] font-mono" :
                    (isLight ? "bg-amber-50 border-amber-200/80 text-amber-700 font-sans" : "bg-zinc-800/90 border-zinc-700 text-amber-400 font-sans")
                  )}>
                    <Coins className={cn("w-3.5 h-3.5", isStalker ? "text-[#4ade80]" : "text-amber-500")} />
                    <span>{item.tokenPrice} NTK</span>
                  </div>
                )}
              </div>

              {/* Card Middle: Icon & Title */}
              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-3">
                  <div className={cn(
                    "w-11 h-11 rounded-xl flex items-center justify-center shrink-0 border",
                    isThemeActive
                      ? (isStalker ? "bg-[#4ade80]/20 border-[#4ade80] text-[#4ade80]" :
                         "bg-emerald-500/20 border-emerald-500 text-emerald-600 dark:text-emerald-400")
                      : isStalker ? "bg-[#4ade80]/10 border-[#4ade80]/30 text-[#4ade80]" :
                      cn(rarityCfg.bg, rarityCfg.border, rarityCfg.text)
                  )}>
                    {item.id === 'item_theme_stalker' || item.icon === 'Radiation' ? <Radiation className="w-5 h-5" /> :
                     item.id === 'item_pro_pass_24h' || item.icon === 'Zap' ? <Zap className="w-5 h-5" /> :
                     item.id === 'item_pro_pass_7d' || item.icon === 'Sparkles' ? <Sparkles className="w-5 h-5" /> :
                     item.category === 'passes' || item.icon === 'Crown' ? <Crown className="w-5 h-5" /> :
                     item.category === 'themes' || item.icon === 'Palette' ? <Palette className="w-5 h-5" /> :
                     item.category === 'crates' || item.icon === 'PackageCheck' ? <PackageCheck className="w-5 h-5" /> :
                     <Shield className="w-5 h-5" />}
                  </div>

                  <div className="min-w-0">
                    <h5 className={cn(
                      "font-bold text-sm tracking-wide leading-tight truncate",
                      isStalker ? "font-mono text-[#4ade80]" :
                      "text-zinc-900 dark:text-white font-sans"
                    )}>
                      {isUA ? item.name.ua : item.name.en}
                    </h5>
                    <span className={cn(
                      "text-[10px] uppercase",
                      isStalker ? "font-mono text-zinc-500" : "text-zinc-400 dark:text-zinc-500 font-sans font-medium"
                    )}>
                      {item.category}
                    </span>
                  </div>
                </div>

                <p className={cn(
                  "text-xs leading-relaxed",
                  isStalker ? "text-[#4ade80]/80 font-mono" :
                  "text-zinc-600 dark:text-zinc-400 font-sans"
                )}>
                  {tone === 'bro' && (isUA ? item.broFlavor.ua : item.broFlavor.en)
                    ? (isUA ? item.broFlavor.ua : item.broFlavor.en)
                    : (isUA ? item.desc.ua : item.desc.en)}
                </p>
              </div>

              {/* Card Bottom: Action Button (Equip vs Buy) */}
              {isThemeOwned ? (
                isThemeActive ? (
                  <div className={cn(
                    "w-full py-2.5 px-4 rounded-xl text-xs uppercase tracking-wider flex items-center justify-center gap-2 border",
                    isStalker ? "bg-[#4ade80]/20 text-[#4ade80] border-[#4ade80] font-mono font-bold" :
                    "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/40 font-sans font-bold shadow-xs"
                  )}>
                    <Check className="w-3.5 h-3.5" />
                    <span>{isUA ? 'АКТИВНА ТЕМА' : 'CURRENTLY ACTIVE'}</span>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => themeId && handleEquipTheme(themeId)}
                    className={cn(
                      "w-full py-2.5 px-4 rounded-xl text-xs uppercase tracking-wider transition-all cursor-pointer flex items-center justify-center gap-2 shadow-sm active:scale-95",
                      isStalker ? "bg-[#4ade80] hover:bg-[#22c55e] text-black font-mono font-bold" :
                      "bg-emerald-500 hover:bg-emerald-600 text-white font-sans font-bold"
                    )}
                  >
                    <Sparkles className="w-3.5 h-3.5" />
                    <span>{isUA ? 'ЗАСТОСУВАТИ ТЕМУ' : 'EQUIP THEME'}</span>
                  </button>
                )
              ) : (
                <button
                  type="button"
                  disabled={!canAfford || isPurchasing === item.id}
                  onClick={() => handlePurchase(item)}
                  className={cn(
                    "w-full py-2.5 px-4 rounded-xl text-xs uppercase tracking-wider transition-all cursor-pointer flex items-center justify-center gap-2",
                    isStalker ? "font-mono font-bold" : "font-sans font-semibold",
                    canAfford
                      ? (isStalker ? "bg-[#4ade80] hover:bg-[#22c55e] text-black active:scale-95" :
                         "bg-emerald-500 hover:bg-emerald-600 text-white shadow-sm active:scale-95")
                      : (isLight ? "bg-zinc-100 text-zinc-400 border border-zinc-200 cursor-not-allowed" : "bg-zinc-800/80 text-zinc-500 cursor-not-allowed border border-zinc-700/50")
                  )}
                >
                  {isPurchasing === item.id ? (
                    <span className="animate-pulse">{isUA ? 'ОБРОБКА...' : 'PROCESSING...'}</span>
                  ) : canAfford ? (
                    <>
                      <ShoppingBag className="w-3.5 h-3.5" />
                      <span>{isUA ? `ПРИДБАТИ ЗА ${item.tokenPrice} NTK` : `BUY FOR ${item.tokenPrice} NTK`}</span>
                    </>
                  ) : (
                    <>
                      <Lock className="w-3.5 h-3.5" />
                      <span>{isUA ? `ПОТРІБНО ${item.tokenPrice} NTK` : `NEEDS ${item.tokenPrice} NTK`}</span>
                    </>
                  )}
                </button>
              )}
            </div>
          );
        })}
      </div>

    </div>
  );
};
